import { first, flatMap, omit, orderBy } from 'lodash-es'
import Info from '@/models/leave/drafting/Info'
import Error from '@/models/leave/drafting/Error'
import Warning from '@/models/leave/drafting/Warning'
import LeaveRequest from '@/models/leave/drafting/LeaveRequest'
import moment from 'moment/moment'

export default class SingleEmploymentLeaveRequest {
  constructor({ employments, messages, total_duration: totalDuration }) {
    this._setLeaveRequests(employments)
    this._setMessages(messages)
    this.totalDuration = totalDuration
  }

  _setLeaveRequests(employments) {
    employments = this._mergeOverlapLeaveErrors(employments)

    this.items = employments.map(employment => new LeaveRequest(employment))
  }

  _mergeOverlapLeaveErrors(employments) {
    employments[0].breakdowns.forEach(breakdown =>
      this._mergeOverlapLeaveError(breakdown)
    )

    return employments
  }

  _mergeOverlapLeaveError(breakdown) {
    const overlapErrorIndex = this._findOverlapErrorIndex(breakdown)

    if (overlapErrorIndex !== -1) {
      const leave = breakdown.messages.errors[overlapErrorIndex].leave

      breakdown.messages.errors[overlapErrorIndex] = omit(
        {
          ...breakdown.messages.errors[overlapErrorIndex],
          leaves: [leave],
        },
        'leave'
      )
    }
  }

  _findOverlapErrorIndex(breakdown) {
    return breakdown.messages.errors.findIndex(
      error => error.code === 'overlap_with_existing_leave'
    )
  }

  _setMessages(messages) {
    this._setErrors(messages.errors)
    this._setWarnings(messages.warnings)
    this._setInformation(messages.info)
  }

  _setErrors(errors) {
    this.errors = errors.map(error => new Error(error))
  }

  _setWarnings(warnings) {
    this.warnings = warnings.map(warning => new Warning(warning))
  }

  _setInformation(info) {
    this.information = info.map(info => new Info(info))
  }

  get isSingleRequest() {
    return this.items.length === 1
  }

  get breakdowns() {
    return this.items[0].breakdowns
  }

  get showCustomDuration() {
    return true
  }

  get employments() {
    return this.items.map(draftLeave => draftLeave.owner)
  }

  getBreakdownsToRequest() {
    return this.items.map(draftedLeave => draftedLeave.getBreakdownsToRequest())
  }

  generateBreakdownsToRedraft(pickedBreakdowns) {
    return this._generateBreakdownsForOwnersUsingPickedBreakdowns(
      pickedBreakdowns
    )
  }

  get all() {
    return this.items
  }

  get containPastDates() {
    return this.warnings.some(warning => warning.isPastDates)
  }

  get hasNoticePeriodPolicyPlanLimitation() {
    return this.warnings.some(
      warning => warning.isNoticePeriodPolicyPlanLimitation
    )
  }

  get noticePeriodPolicyPlanLimitationWarning() {
    return this.warnings.find(
      warning => warning.isNoticePeriodPolicyPlanLimitation
    )
  }

  get doesntHaveCalendarToRequestLeave() {
    return this.errors.some(error => error.isCalendarNotSetup)
  }

  get datesOverlapOnDifferentCalendars() {
    return this.errors.some(error => error.isCrossBoundaries)
  }

  get hasRemainingAllowancesExceeded() {
    return this.errors.some(error => error.isAllowanceInsufficient)
  }

  get hasNoticePeriodPolicyViolation() {
    return this.errors.some(error => error.isNoticePeriodPolicyViolation)
  }

  get noticePeriodPolicyError() {
    let errorWithHighestNoticePeriod = null

    for (const leaveRequest of this.items) {
      const noticePeriodError = leaveRequest.errors?.find(
        error => error.isNoticePeriodPolicyViolation
      )

      const hasHigherNoticePeriod =
        noticePeriodError?.noticePeriodDays >
        errorWithHighestNoticePeriod?.noticePeriodDays

      if (
        noticePeriodError &&
        (!errorWithHighestNoticePeriod || hasHigherNoticePeriod)
      ) {
        errorWithHighestNoticePeriod = noticePeriodError
      }
    }

    return errorWithHighestNoticePeriod
  }

  get hasNoticePeriodPolicyWarning() {
    return this.items.some(leaveRequest =>
      leaveRequest.warnings?.some(
        warning => warning.isNoticePeriodPolicyWarning
      )
    )
  }

  get noticePeriodPolicyWarning() {
    let warningWithHighestNoticePeriod = null

    for (const leaveRequest of this.items) {
      const noticePeriodPolicyWarning = leaveRequest.warnings?.find(
        warning => warning.isNoticePeriodPolicyWarning
      )

      const hasHigherNoticePeriod =
        noticePeriodPolicyWarning?.noticePeriodDays >
        warningWithHighestNoticePeriod?.noticePeriodDays

      if (
        noticePeriodPolicyWarning &&
        (!warningWithHighestNoticePeriod || hasHigherNoticePeriod)
      ) {
        warningWithHighestNoticePeriod = noticePeriodPolicyWarning
      }
    }

    return warningWithHighestNoticePeriod
  }

  get hasBookingPeriodPolicyViolation() {
    return this.errors.some(error => error.isBookingPeriodPolicyViolation)
  }

  get bookingPeriodPolicyError() {
    for (const leaveRequest of this.items) {
      const error = leaveRequest.errors?.find(
        error => error.isBookingPeriodPolicyViolation
      )

      if (error) {
        return error
      }
    }

    return null
  }

  get hasBookingPeriodPolicyOnBehalfWarning() {
    return this.items.some(leaveRequest =>
      leaveRequest.warnings?.some(
        warning => warning.isBookingPeriodPolicyOnBehalfWarning
      )
    )
  }

  get bookingPeriodPolicyOnBehalfWarning() {
    for (const leaveRequest of this.items) {
      const warning = leaveRequest.warnings?.find(
        warning => warning.isBookingPeriodPolicyOnBehalfWarning
      )

      if (warning) {
        return warning
      }
    }

    return null
  }

  get hasBookingPeriodPolicyPlanLimitation() {
    return this.warnings.some(
      warning => warning.isBookingPeriodPolicyPlanLimitation
    )
  }

  get bookingPeriodPolicyPlanLimitationWarning() {
    return this.warnings.find(
      warning => warning.isBookingPeriodPolicyPlanLimitation
    )
  }

  get outsideEmploymentPeriod() {
    return this.errors.some(error => error.isOutsideOfEmploymentPeriod)
  }

  get hasMaximumLeaveRequestPolicyViolation() {
    return this.items.some(leaveRequest =>
      leaveRequest.errors?.some(
        error => error.isMaximumLeaveRequestPolicyViolation
      )
    )
  }

  get maximumLeaveRequestPolicyError() {
    for (const leaveRequest of this.items) {
      const error = leaveRequest.errors?.find(
        error => error.isMaximumLeaveRequestPolicyViolation
      )

      if (error) {
        return error
      }
    }

    return null
  }

  get hasMaximumLeaveRequestPolicyWarning() {
    return this.items.some(leaveRequest =>
      leaveRequest.warnings?.some(
        warning => warning.isMaximumLeaveRequestPolicyWarning
      )
    )
  }

  get maximumLeaveRequestPolicyWarning() {
    for (const leaveRequest of this.items) {
      const warning = leaveRequest.warnings?.find(
        warning => warning.isMaximumLeaveRequestPolicyWarning
      )

      if (warning) {
        return warning
      }
    }

    return null
  }

  get hasMaximumLeaveRequestPolicyPlanLimitation() {
    return this.warnings.some(
      warning => warning.isMaximumLeaveRequestPolicyPlanLimitation
    )
  }

  get maximumLeaveRequestPolicyPlanLimitationWarning() {
    return this.warnings.find(
      warning => warning.isMaximumLeaveRequestPolicyPlanLimitation
    )
  }

  get hasUnlimitedAllowances() {
    return this.information.some(info => info.isAllowanceUnlimited)
  }

  get selectedLeaveTypeDoesntHaveAllowances() {
    return this.information.some(info => info.isAllowanceNotSetupForLeaveType)
  }

  get containsHoursPerWorkingDayExceededWarning() {
    return this.breakdowns.some(breakdown =>
      breakdown.warnings.some(warning => warning.isHoursPerWorkingDayExceeded)
    )
  }

  get containsLeaveLimitReachedWarning() {
    return this.breakdowns.some(breakdown =>
      breakdown.warnings.some(warning => warning.isLeaveLimitReached)
    )
  }

  get overlapWithExistingLeave() {
    return this.breakdowns.some(breakdown =>
      breakdown.errors.some(error => error.isOverlapWithExistingLeave)
    )
  }

  get cannotRequestLeaveWhenLeaveLimitExceeded() {
    return this.breakdowns.some(breakdown =>
      breakdown.errors.some(error => error.isLeaveLimitReached)
    )
  }

  findMinimumFromTime(pickedBreakdowns) {
    const breakdown = this._findMinimumBreakdownTimeUsingBreakdownsForOwners(
      pickedBreakdowns
    )

    return this._makeTime(breakdown.from)
  }

  _findMinimumBreakdownTimeUsingBreakdownsForOwners(pickedBreakdowns) {
    const ownersWithBreakdowns = this._generateBreakdownsForOwnersUsingPickedBreakdowns(
      pickedBreakdowns
    )

    const breakdowns = this._getAllBreakdownsWithoutOwners(ownersWithBreakdowns)

    return first(this._orderBreakdownFromTimeInAscending(breakdowns))
  }

  _orderBreakdownFromTimeInAscending(breakdowns) {
    return this._orderBreakdowns(breakdowns, 'from', 'asc')
  }

  findMaximumToTime(pickedBreakdowns) {
    const breakdown = this._findMaximumBreakdownTimeUsingBreakdownsForOwners(
      pickedBreakdowns
    )

    return this._makeTime(breakdown.to)
  }

  _findMaximumBreakdownTimeUsingBreakdownsForOwners(pickedBreakdowns) {
    const ownersWithBreakdowns = this._generateBreakdownsForOwnersUsingPickedBreakdowns(
      pickedBreakdowns
    )

    const breakdowns = this._getAllBreakdownsWithoutOwners(ownersWithBreakdowns)

    return first(this._orderBreakdownToTimeInDescending(breakdowns))
  }

  _orderBreakdownToTimeInDescending(breakdowns) {
    return this._orderBreakdowns(breakdowns, 'to', 'desc')
  }

  _getAllBreakdownsWithoutOwners(ownersWithBreakdowns) {
    return flatMap(ownersWithBreakdowns, 'breakdowns')
  }

  _orderBreakdowns(breakdowns, field, order) {
    return orderBy(breakdowns, [field], [order])
  }

  _makeTime(time) {
    return moment.utc(time)
  }

  get totalSelectedDurationsInUnits() {
    return this.totalDuration
  }

  _generateBreakdownsForOwnersUsingPickedBreakdowns(pickedBreakdowns) {
    return this.items.map(draftedLeave =>
      this._generateSuitableBreakdown(pickedBreakdowns, draftedLeave)
    )
  }

  _generateSuitableBreakdown(pickedBreakdowns, draftedLeave) {
    return {
      owner_id: draftedLeave.owner.getKey(),
      breakdowns: draftedLeave.breakdowns.map(breakdown => {
        const pickedBreakdown = pickedBreakdowns.find(pickedBreakdown =>
          pickedBreakdown.isSameDate(breakdown.date)
        )

        const duration = pickedBreakdown.activeDuration

        return {
          type: breakdown.type,
          ...duration.toJson(),
        }
      }),
    }
  }
}
